import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form as AntForm,
  Input,
  InputNumber,
  Space,
} from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { Translator } from '@/i18n/config';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { categoriesApi } from '@api/categories';
import { recordAuditAction } from '@features/audit/recordAction';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { useActiveLanguages, pickDefaultLanguage } from '@features/languages/useLanguages';
import { CategoryPicker } from './CategoryPicker';
import { getDescendantIds } from '../utils/tree';
import type { Category } from '../types';

const SLUG_RULE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function makeSchema(t: Translator) {
  return z.object({
    name: z.string().min(2, t('categories:form.validation.nameMin')).max(80),
    slug: z
      .string()
      .min(2, t('categories:form.validation.slugMin'))
      .max(80)
      .regex(SLUG_RULE, t('categories:form.validation.slugFormat')),
    parentId: z.string().nullable(),
    description: z.string().max(280).optional(),
    order: z.number().int().min(0).optional(),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

interface Props {
  open: boolean;
  category: Category | null;
  initialParentId?: string | null;
  allCategories: Category[];
  onClose: () => void;
}

export function CategoryDrawer({
  open,
  category,
  initialParentId,
  allCategories,
  onClose,
}: Props) {
  const { t } = useTranslation(['categories', 'common', 'validation', 'errors']);
  const isEdit = !!category;
  const qc = useQueryClient();

  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);
  const { data: activeLanguages = [] } = useActiveLanguages();
  // İçerik dili (yalnız create) — effect'siz türetme (set-state-in-effect'ten kaçınır).
  const [pickedLanguageId, setPickedLanguageId] = useState<string | undefined>(undefined);
  const languageId = pickedLanguageId ?? pickDefaultLanguage(activeLanguages)?.id;

  const disabledParentIds = useMemo(() => {
    if (!category) return [] as string[];
    return [category.id, ...getDescendantIds(allCategories, category.id)];
  }, [allCategories, category]);

  const initialValues = useMemo<Values>(
    () =>
      category
        ? {
            name: category.name,
            slug: category.slug,
            parentId: category.parentId,
            description: category.description ?? '',
            order: category.order,
          }
        : {
            name: '',
            slug: '',
            parentId: initialParentId ?? null,
            description: '',
            order: undefined,
          },
    [category, initialParentId],
  );

  const createMutation = useMutation({
    mutationFn: (values: Values) =>
      categoriesApi.create({
        name: values.name,
        slug: values.slug,
        parentId: values.parentId,
        description: values.description || undefined,
        order: values.order,
        languageId: languageId ?? null,
      }),
    onSuccess: (created) => {
      toast.success(t('toast.created', { name: created.name }));
      void recordAuditAction({
        action: 'category.create',
        resource: 'category',
        resourceId: created.id,
        summary: t('audit.createSummary', { name: created.name }),
        meta: { slug: created.slug },
      });
      void qc.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: Values) => {
      if (!category) throw new Error('no category');
      return categoriesApi.update(category.id, {
        name: values.name,
        slug: values.slug,
        parentId: values.parentId,
        description: values.description || undefined,
        order: values.order,
      });
    },
    onSuccess: (updated) => {
      toast.success(t('toast.updated', { name: updated.name }));
      void recordAuditAction({
        action: 'category.update',
        resource: 'category',
        resourceId: updated.id,
        summary: t('audit.updateSummary', { name: updated.name }),
        meta: { slug: updated.slug },
      });
      void qc.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  useEffect(() => {
    if (!open) {
      createMutation.reset();
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Drawer
      title={isEdit ? t('drawer.editTitle', { name: category?.name }) : t('drawer.createTitle')}
      open={open}
      onClose={onClose}
      width={Math.min(540, typeof window !== 'undefined' ? window.innerWidth : 540)}
      destroyOnClose
    >
      <Form
        schema={schema}
        defaultValues={initialValues}
        onSubmit={(v) => (isEdit ? updateMutation.mutate(v) : createMutation.mutate(v))}
      >
        {({ watch, setValue }) => {
          const name = watch('name');
          const slug = watch('slug');
          const parentId = watch('parentId');
          return (
            <>
              <AntForm layout="vertical" component="div">
                {!isEdit ? (
                  <AntForm.Item
                    label={t('common:contentLanguage.label')}
                    help={t('form.languageHint')}
                  >
                    <LanguagePicker
                      value={languageId}
                      onChange={setPickedLanguageId}
                      style={{ width: '100%' }}
                    />
                  </AntForm.Item>
                ) : null}
                <FormField name="name" label={t('form.labels.name')} required>
                  <Input size="large" placeholder={t('form.placeholders.name')} />
                </FormField>
                <AntForm.Item label={t('form.labels.slug')} required>
                  <SlugInput
                    value={slug}
                    onChange={(v) => setValue('slug', v, { shouldDirty: true, shouldValidate: true })}
                    sourceValue={name}
                  />
                </AntForm.Item>
                <AntForm.Item label={t('form.labels.parent')}>
                  <CategoryPicker
                    value={parentId ?? undefined}
                    onChange={(v) =>
                      setValue('parentId', (v as string | undefined) ?? null, {
                        shouldDirty: true,
                      })
                    }
                    disabledIds={disabledParentIds}
                    placeholder={t('form.placeholders.parent')}
                  />
                </AntForm.Item>
                <FormField name="description" label={t('form.labels.description')} help={t('form.help.description')}>
                  <Input.TextArea rows={3} maxLength={280} showCount />
                </FormField>
                <FormField name="order" label={t('form.labels.order')}>
                  <InputNumber min={0} style={{ width: 160 }} placeholder={t('form.placeholders.order')} />
                </FormField>
              </AntForm>
              <Space style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {isEdit ? t('common:actions.save') : t('common:actions.create')}
                </Button>
                <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
              </Space>
            </>
          );
        }}
      </Form>
    </Drawer>
  );
}
